import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { EXPENSES, BUDGETS } from "@/constants/testIds";
import { toast, softDeleteWithUndo } from "@/lib/notify";
import { Trash2, Plus, Save, AlertTriangle, ChevronDown, ChevronUp, Target } from "lucide-react";

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const PAYMENT_METHODS = ["Efectivo", "Transferencia", "Cheque", "Tarjeta", "Otro"];

const FALLBACK_CATEGORIES = [
  "Ayuda/ofrendas miembros",
  "Comida para la comunidad",
  "Renta de Local",
  "Servicios (luz/agua/internet)",
  "Materiales/Suministros",
  "Diezmo a cobertura",
  "Misiones",
  "Mantenimiento",
  "Otros",
];

const now = new Date();

const fmt = (n) =>
  Number(n || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const firstOfMonthISO = (year, month) =>
  `${year}-${String(month).padStart(2, "0")}-01`;

const EMPTY_ROW = (date) => ({
  id: null,
  _tmpId: `tmp-${Math.random().toString(36).slice(2)}`,
  date: date || todayISO(),
  category: "Otros",
  description: "",
  amount: 0,
  payment_method: "Efectivo",
  notes: "",
  _dirty: true,
});

export default function Gastos() {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Budgets & Alerts (Phase B)
  const [budgetsOpen, setBudgetsOpen] = useState(true);
  const [budgetItems, setBudgetItems] = useState([]); // [{category, amount, consumed, percentage, alert_level, id?}]
  const [budgetTotals, setBudgetTotals] = useState({ budget: 0, consumed: 0, percentage: 0, alert_level: "ok", remaining: 0 });
  const [budgetDrafts, setBudgetDrafts] = useState({}); // {category: amountString}
  const [budgetSaving, setBudgetSaving] = useState(false);
  // Configurable thresholds (global)
  const [thresholds, setThresholds] = useState({ warning: 80, critical: 100 });
  const [thresholdDrafts, setThresholdDrafts] = useState({ warning: "80", critical: "100" });
  const [thresholdSaving, setThresholdSaving] = useState(false);

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expenses", { params: { year, month } });
      const mapped = data.map((d) => ({ ...d, _dirty: false, _tmpId: null }));
      mapped.sort((a, b) => (a.date < b.date ? -1 : 1));
      setRows(mapped);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  const loadBudgets = useCallback(async () => {
    try {
      const { data } = await api.get("/budgets", { params: { year, month } });
      setBudgetItems(data.items || []);
      setBudgetTotals(data.totals || { budget: 0, consumed: 0, percentage: 0, alert_level: "ok", remaining: 0 });
      // Reset drafts to match current saved values
      const drafts = {};
      (data.items || []).forEach((it) => {
        drafts[it.category] = String(it.amount || 0);
      });
      setBudgetDrafts(drafts);
      // Pick up thresholds from same response (server applies them)
      if (data.thresholds) {
        const w = Number(data.thresholds.warning) || 80;
        const c = Number(data.thresholds.critical) || 100;
        setThresholds({ warning: w, critical: c });
        setThresholdDrafts({ warning: String(w), critical: String(c) });
      }
    } catch (e) {
      // Silent: budgets are optional
      setBudgetItems([]);
      setBudgetTotals({ budget: 0, consumed: 0, percentage: 0, alert_level: "ok", remaining: 0 });
      setBudgetDrafts({});
    }
  }, [year, month]);

  useEffect(() => {
    api
      .get("/expense-categories")
      .then(({ data }) => setCategories(data.categories || FALLBACK_CATEGORIES))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadBudgets();
  }, [loadBudgets]);

  // Recompute budget consumption locally after expenses change
  const consumedByCategory = useMemo(() => {
    const map = {};
    for (const r of rows) {
      const cat = r.category || "Otros";
      map[cat] = (map[cat] || 0) + (Number(r.amount) || 0);
    }
    return map;
  }, [rows]);

  const budgetView = useMemo(() => {
    const warn = Number(thresholds.warning) || 80;
    const crit = Number(thresholds.critical) || 100;
    // Use categories as the row set so user can budget for any expense category
    const items = categories.map((cat) => {
      const draftStr = budgetDrafts[cat];
      const amount = draftStr !== undefined && draftStr !== "" ? Number(draftStr) : 0;
      const saved = budgetItems.find((it) => it.category === cat);
      const savedAmount = saved ? Number(saved.amount) : 0;
      const consumed = Number(consumedByCategory[cat] || 0);
      const percentage = amount > 0 ? (consumed / amount) * 100 : 0;
      let level = "ok";
      if (amount > 0) {
        if (percentage >= crit) level = "exceeded";
        else if (percentage >= warn) level = "warning";
      }
      return {
        category: cat,
        id: saved?.id,
        amount,
        savedAmount,
        consumed,
        percentage,
        remaining: amount - consumed,
        level,
        dirty: saved ? Math.abs(amount - savedAmount) > 0.001 : amount > 0,
      };
    });
    const totalBudget = items.reduce((acc, it) => acc + it.amount, 0);
    const totalConsumed = items.reduce((acc, it) => acc + it.consumed, 0);
    const totalPct = totalBudget > 0 ? (totalConsumed / totalBudget) * 100 : 0;
    let totalLevel = "ok";
    if (totalBudget > 0) {
      if (totalPct >= crit) totalLevel = "exceeded";
      else if (totalPct >= warn) totalLevel = "warning";
    }
    const alerts = items.filter((it) => it.amount > 0 && (it.level === "warning" || it.level === "exceeded"));
    return { items, totalBudget, totalConsumed, totalPct, totalLevel, alerts };
  }, [categories, budgetDrafts, budgetItems, consumedByCategory, thresholds]);

  const budgetDirtyCount = budgetView.items.filter((it) => it.dirty).length;

  const addEmptyRow = () => {
    setRows((prev) => [...prev, EMPTY_ROW(firstOfMonthISO(year, month))]);
  };

  const updateRow = (key, field, value) => {
    setRows((prev) =>
      prev.map((r) =>
        (r.id || r._tmpId) === key ? { ...r, [field]: value, _dirty: true } : r
      )
    );
  };

  const removeRow = (row) => {
    const key = row.id || row._tmpId;
    setRows((prev) => prev.filter((r) => (r.id || r._tmpId) !== key));
    if (!row.id) return;
    softDeleteWithUndo({
      message: "Gasto eliminado",
      description: `${row.date} · ${row.category} · $${fmt(row.amount)}`,
      onUndo: () => {
        setRows((prev) => {
          if (prev.some((r) => r.id === row.id)) return prev;
          return [...prev, row].sort((a, b) => (a.date < b.date ? -1 : 1));
        });
      },
      onDelete: async () => {
        try {
          await api.delete(`/expenses/${row.id}`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Error eliminando — restaurando");
          setRows((prev) => {
            if (prev.some((r) => r.id === row.id)) return prev;
            return [...prev, row].sort((a, b) => (a.date < b.date ? -1 : 1));
          });
        }
      },
    });
  };

  const saveAll = async () => {
    const dirty = rows.filter((r) => r._dirty);
    if (!dirty.length) {
      toast.info("Sin cambios para guardar.");
      return;
    }
    setSaving(true);
    try {
      const updated = [...rows];
      for (const row of dirty) {
        const payload = {
          date: row.date,
          category: row.category,
          description: row.description || "",
          amount: Number(row.amount) || 0,
          payment_method: row.payment_method,
          notes: row.notes || "",
        };
        let saved;
        if (row.id) {
          const { data } = await api.put(`/expenses/${row.id}`, payload);
          saved = data;
        } else {
          const { data } = await api.post("/expenses", payload);
          saved = data;
        }
        const idx = updated.findIndex(
          (r) => (r.id || r._tmpId) === (row.id || row._tmpId)
        );
        if (idx !== -1) updated[idx] = { ...saved, _dirty: false, _tmpId: null };
      }
      setRows(updated);
      toast.success(
        `Guardado: ${dirty.length} gasto${dirty.length === 1 ? "" : "s"}.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => {
    const total = rows.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
    const byCat = {};
    for (const r of rows) {
      const cat = r.category || "Otros";
      byCat[cat] = (byCat[cat] || 0) + (Number(r.amount) || 0);
    }
    const breakdown = Object.entries(byCat).sort((a, b) => b[1] - a[1]);
    return { total, breakdown };
  }, [rows]);

  const saveBudgets = async () => {
    const dirty = budgetView.items.filter((it) => it.dirty);
    if (!dirty.length) {
      toast.info("Sin cambios en presupuestos.");
      return;
    }
    setBudgetSaving(true);
    try {
      for (const it of dirty) {
        if (it.amount <= 0 && it.id) {
          await api.delete(`/budgets/${it.id}`);
        } else if (it.amount > 0) {
          await api.post("/budgets", {
            year,
            month,
            category: it.category,
            amount: Number(it.amount) || 0,
            notes: "",
          });
        }
      }
      await loadBudgets();
      toast.success(`Presupuestos guardados: ${dirty.length}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando presupuestos");
    } finally {
      setBudgetSaving(false);
    }
  };

  const updateBudgetDraft = (category, value) => {
    setBudgetDrafts((prev) => ({ ...prev, [category]: value }));
  };

  const updateThresholdDraft = (key, value) => {
    setThresholdDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const saveThresholds = async () => {
    const warn = Number(thresholdDrafts.warning);
    const crit = Number(thresholdDrafts.critical);
    if (!Number.isFinite(warn) || warn <= 0 || warn > 200) {
      toast.error("Advertencia debe ser entre 1 y 200.");
      return;
    }
    if (!Number.isFinite(crit) || crit <= 0 || crit > 500) {
      toast.error("Crítico debe ser entre 1 y 500.");
      return;
    }
    if (warn >= crit) {
      toast.error("Advertencia debe ser menor que crítico.");
      return;
    }
    setThresholdSaving(true);
    try {
      const { data } = await api.put("/settings/budget-thresholds", { warning: warn, critical: crit });
      setThresholds({ warning: Number(data.warning), critical: Number(data.critical) });
      setThresholdDrafts({ warning: String(data.warning), critical: String(data.critical) });
      await loadBudgets();
      toast.success("Umbrales guardados.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando umbrales");
    } finally {
      setThresholdSaving(false);
    }
  };

  const dirtyCount = rows.filter((r) => r._dirty).length;

  return (
    <div data-testid={EXPENSES.page}>
      <PageHeader
        title="Gastos"
        subtitle="Salidas por categoría · Libro contable estilo Excel"
        actions={
          <div className="flex items-center gap-2">
            <button
              data-testid={EXPENSES.addRowButton}
              onClick={addEmptyRow}
              className="flex items-center gap-2 border border-[#d9cfb8] bg-white text-[#5d5849] hover:bg-[#fbf7ee] px-4 py-2.5 text-xs uppercase tracking-widest"
            >
              <Plus size={14} /> Gasto
            </button>
            <button
              data-testid={EXPENSES.saveAllButton}
              onClick={saveAll}
              disabled={saving}
              className="flex items-center gap-2 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] px-5 py-2.5 text-xs uppercase tracking-widest disabled:opacity-60"
            >
              <Save size={14} />
              {saving ? "Guardando..." : `Guardar ${dirtyCount ? `(${dirtyCount})` : ""}`}
            </button>
          </div>
        }
      />

      <div className="p-8 space-y-4">
        {/* Filters */}
        <div
          className="bg-white border border-[#d9cfb8] px-5 py-4 flex flex-wrap items-end gap-6"
          style={{ borderTop: "3px solid #c8a04a" }}
        >
          <Field label="Mes">
            <select
              data-testid={EXPENSES.monthSelect}
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className={selectCls}
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </Field>
          <Field label="Año">
            <select
              data-testid={EXPENSES.yearSelect}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className={selectCls}
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </Field>

          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">
              Total del mes
            </div>
            <div className="font-serif text-2xl text-[#a14040]">$ {fmt(totals.total)}</div>
          </div>
        </div>

        {/* Spreadsheet */}
        <div className="overflow-x-auto border border-[#d9cfb8] bg-white">
          <table className="ajar-spreadsheet" data-testid={EXPENSES.table}>
            <thead>
              <tr>
                <th style={{ width: 130 }}>Fecha</th>
                <th style={{ width: 220 }}>Categoría</th>
                <th>Descripción</th>
                <th className="text-right" style={{ width: 130 }}>Monto</th>
                <th style={{ width: 140 }}>Forma de pago</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-[#8b7e5e]">
                    Cargando...
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-[#8b7e5e]">
                    Sin gastos para {MONTHS[month - 1]} {year}. Pulsa{" "}
                    <strong>Gasto</strong> para agregar uno.
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => {
                  const key = r.id || r._tmpId;
                  return (
                    <tr key={key} data-testid={EXPENSES.row(key)}>
                      <td>
                        <input
                          data-testid={EXPENSES.dateInput(key)}
                          type="date"
                          value={r.date}
                          onChange={(e) => updateRow(key, "date", e.target.value)}
                          className="ajar-input"
                        />
                      </td>
                      <td>
                        <select
                          data-testid={EXPENSES.categorySelect(key)}
                          value={r.category}
                          onChange={(e) => updateRow(key, "category", e.target.value)}
                          className="ajar-input"
                        >
                          {categories.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          data-testid={EXPENSES.descriptionInput(key)}
                          value={r.description || ""}
                          onChange={(e) => updateRow(key, "description", e.target.value)}
                          className="ajar-input"
                          placeholder="Detalle del gasto"
                        />
                      </td>
                      <td className="text-right">
                        <input
                          data-testid={EXPENSES.amountInput(key)}
                          type="number"
                          step="0.01"
                          min="0"
                          value={r.amount === 0 ? "" : r.amount}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const n = raw === "" ? 0 : parseFloat(raw);
                            updateRow(key, "amount", Number.isFinite(n) ? n : 0);
                          }}
                          onFocus={(e) => e.target.select()}
                          placeholder="0.00"
                          className="ajar-input text-right tabular-nums"
                        />
                      </td>
                      <td>
                        <select
                          data-testid={EXPENSES.methodSelect(key)}
                          value={r.payment_method}
                          onChange={(e) =>
                            updateRow(key, "payment_method", e.target.value)
                          }
                          className="ajar-input"
                        >
                          {PAYMENT_METHODS.map((m) => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                      </td>
                      <td className="text-center">
                        <button
                          data-testid={EXPENSES.deleteButton(key)}
                          onClick={() => removeRow(r)}
                          className="text-[#a14040] hover:text-red-800"
                          title="Eliminar gasto"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr data-testid={EXPENSES.totalsRow}>
                  <td className="ajar-cell-total" colSpan={3}>
                    TOTAL
                  </td>
                  <td className="ajar-cell-total text-right">$ {fmt(totals.total)}</td>
                  <td className="ajar-cell-total" colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Category breakdown */}
        {totals.breakdown.length > 0 && (
          <div
            className="bg-white border border-[#d9cfb8] p-5"
            style={{ borderTop: "3px solid #c8a04a" }}
          >
            <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-3">
              Desglose por categoría
            </div>
            <div className="space-y-2">
              {totals.breakdown.map(([cat, amount]) => {
                const pct = totals.total > 0 ? (amount / totals.total) * 100 : 0;
                return (
                  <div key={cat} className="text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[#1c1a14]">{cat}</span>
                      <span className="tabular-nums font-medium">
                        $ {fmt(amount)}{" "}
                        <span className="text-[#8b7e5e] text-xs">
                          ({pct.toFixed(1)}%)
                        </span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-[#f1ead7]">
                      <div
                        className="h-1.5 bg-[#c8a04a]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Budgets & Alerts (Phase B) */}
        <BudgetsPanel
          open={budgetsOpen}
          setOpen={setBudgetsOpen}
          view={budgetView}
          drafts={budgetDrafts}
          onChangeDraft={updateBudgetDraft}
          onSave={saveBudgets}
          saving={budgetSaving}
          dirtyCount={budgetDirtyCount}
          monthLabel={`${MONTHS[month - 1]} ${year}`}
          thresholds={thresholds}
          thresholdDrafts={thresholdDrafts}
          onChangeThreshold={updateThresholdDraft}
          onSaveThresholds={saveThresholds}
          thresholdSaving={thresholdSaving}
        />
      </div>
    </div>
  );
}

const selectCls =
  "bg-white border border-[#d9cfb8] px-3 py-2 text-sm text-[#1c1a14] focus:outline-none focus:border-[#c8a04a]";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

const LEVEL_BAR = {
  ok: "#5b8c5a",       // green
  warning: "#c8a04a",  // gold
  exceeded: "#a14040", // red
};

const LEVEL_BG = {
  ok: "bg-[#f0f4ec]",
  warning: "bg-[#fdf7e1]",
  exceeded: "bg-[#fbeeee]",
};

const LEVEL_LABEL = {
  ok: "En rango",
  warning: "Cerca del límite",
  exceeded: "Excedido",
};

function BudgetsPanel({ open, setOpen, view, drafts, onChangeDraft, onSave, saving, dirtyCount, monthLabel, thresholds, thresholdDrafts, onChangeThreshold, onSaveThresholds, thresholdSaving }) {
  const hasAlerts = view.alerts.length > 0;
  const thresholdsDirty =
    Number(thresholdDrafts.warning) !== Number(thresholds.warning) ||
    Number(thresholdDrafts.critical) !== Number(thresholds.critical);

  return (
    <div
      data-testid={BUDGETS.panel}
      className="bg-white border border-[#d9cfb8]"
      style={{ borderTop: "3px solid #1c2a3a" }}
    >
      <button
        data-testid={BUDGETS.toggle}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#fbf7ee]"
      >
        <div className="flex items-center gap-3">
          <Target size={16} className="text-[#1c2a3a]" />
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">
              Presupuestos & Alertas
            </div>
            <div className="font-serif text-lg text-[#1c1a14]">
              {monthLabel}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {view.totalBudget > 0 && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">
                Consumido / Presupuesto
              </div>
              <div className="text-sm tabular-nums font-medium">
                $ {fmt(view.totalConsumed)} / $ {fmt(view.totalBudget)}{" "}
                <span
                  className="text-xs ml-1"
                  style={{ color: LEVEL_BAR[view.totalLevel] }}
                >
                  ({view.totalPct.toFixed(1)}%)
                </span>
              </div>
            </div>
          )}
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>

      {open && (
        <div className="border-t border-[#d9cfb8] p-5 space-y-4">
          {hasAlerts && (
            <div
              data-testid={BUDGETS.alertsBanner}
              className="flex items-start gap-2 px-4 py-3 bg-[#fdf7e1] border border-[#e7c879]"
            >
              <AlertTriangle size={16} className="text-[#7a5b0f] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-[#7a5b0f]">
                <div className="font-medium mb-1">
                  {view.alerts.length} categoría{view.alerts.length === 1 ? "" : "s"} requieren atención
                </div>
                <ul className="text-xs space-y-0.5">
                  {view.alerts.map((a) => (
                    <li key={a.category}>
                      <strong>{a.category}</strong>: $ {fmt(a.consumed)} / $ {fmt(a.amount)}{" "}
                      ({a.percentage.toFixed(1)}%) —{" "}
                      <span style={{ color: LEVEL_BAR[a.level] }}>
                        {LEVEL_LABEL[a.level]}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Configurable thresholds */}
          <div className="flex flex-wrap items-end gap-4 px-4 py-3 bg-[#fbf7ee] border border-[#e8dfc4]">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
                Umbrales de alerta (global)
              </div>
              <div className="flex items-end gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">Advertencia (%)</span>
                  <input
                    data-testid={BUDGETS.warningInput}
                    type="number"
                    step="1"
                    min="1"
                    max="200"
                    value={thresholdDrafts.warning}
                    onChange={(e) => onChangeThreshold("warning", e.target.value)}
                    className="ajar-input tabular-nums w-24 text-right block mt-1"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">Crítico (%)</span>
                  <input
                    data-testid={BUDGETS.criticalInput}
                    type="number"
                    step="1"
                    min="1"
                    max="500"
                    value={thresholdDrafts.critical}
                    onChange={(e) => onChangeThreshold("critical", e.target.value)}
                    className="ajar-input tabular-nums w-24 text-right block mt-1"
                  />
                </label>
                <button
                  data-testid={BUDGETS.thresholdsSave}
                  onClick={onSaveThresholds}
                  disabled={thresholdSaving || !thresholdsDirty}
                  className="flex items-center gap-2 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] px-4 py-2 text-[10px] uppercase tracking-widest disabled:opacity-60"
                >
                  <Save size={12} />
                  {thresholdSaving ? "Guardando..." : "Guardar umbrales"}
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[#8b7e5e] border-b border-[#d9cfb8]">
                  <th className="text-left py-2 px-2">Categoría</th>
                  <th className="text-right py-2 px-2" style={{ width: 140 }}>Presupuesto</th>
                  <th className="text-right py-2 px-2" style={{ width: 130 }}>Consumido</th>
                  <th className="text-right py-2 px-2" style={{ width: 120 }}>Restante</th>
                  <th className="text-left py-2 px-2" style={{ width: 220 }}>Progreso</th>
                  <th className="text-center py-2 px-2" style={{ width: 110 }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {view.items.map((it) => {
                  const pctCapped = Math.min(it.percentage, 100);
                  const draftVal = drafts[it.category] ?? "";
                  return (
                    <tr
                      key={it.category}
                      data-testid={BUDGETS.row(it.category)}
                      className={`border-b border-[#f1ead7] ${it.amount > 0 ? LEVEL_BG[it.level] : ""}`}
                    >
                      <td className="py-2 px-2 text-[#1c1a14]">{it.category}</td>
                      <td className="py-2 px-2 text-right">
                        <input
                          data-testid={BUDGETS.amountInput(it.category)}
                          type="number"
                          step="0.01"
                          min="0"
                          value={draftVal === "0" ? "" : draftVal}
                          onChange={(e) => onChangeDraft(it.category, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          placeholder="0.00"
                          className="ajar-input text-right tabular-nums w-full"
                        />
                      </td>
                      <td
                        data-testid={BUDGETS.consumed(it.category)}
                        className="py-2 px-2 text-right tabular-nums text-[#1c1a14]"
                      >
                        $ {fmt(it.consumed)}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums">
                        <span
                          style={{
                            color: it.amount > 0 && it.remaining < 0 ? LEVEL_BAR.exceeded : "#1c1a14",
                          }}
                        >
                          $ {fmt(it.remaining)}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        {it.amount > 0 ? (
                          <div>
                            <div
                              data-testid={BUDGETS.bar(it.category)}
                              className="h-2 bg-[#f1ead7] relative overflow-hidden"
                            >
                              <div
                                className="h-2 transition-all"
                                style={{
                                  width: `${pctCapped}%`,
                                  backgroundColor: LEVEL_BAR[it.level],
                                }}
                              />
                            </div>
                            <div
                              data-testid={BUDGETS.percentage(it.category)}
                              className="text-[10px] mt-0.5 tabular-nums"
                              style={{ color: LEVEL_BAR[it.level] }}
                            >
                              {it.percentage.toFixed(1)}%
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-[#8b7e5e]">Sin presupuesto</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-center">
                        {it.amount > 0 ? (
                          <span
                            data-testid={BUDGETS.level(it.category)}
                            className="text-[10px] uppercase tracking-widest px-2 py-1"
                            style={{
                              color: LEVEL_BAR[it.level],
                              borderBottom: `2px solid ${LEVEL_BAR[it.level]}`,
                            }}
                          >
                            {LEVEL_LABEL[it.level]}
                          </span>
                        ) : (
                          <span className="text-xs text-[#8b7e5e]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {view.totalBudget > 0 && (
                <tfoot>
                  <tr
                    data-testid={BUDGETS.totalsRow}
                    className="font-medium border-t-2 border-[#1c2a3a]"
                  >
                    <td className="py-2 px-2 text-[10px] uppercase tracking-widest text-[#1c2a3a]">
                      Total
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">
                      $ {fmt(view.totalBudget)}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">
                      $ {fmt(view.totalConsumed)}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">
                      <span
                        style={{
                          color: view.totalLevel === "exceeded" ? LEVEL_BAR.exceeded : "#1c1a14",
                        }}
                      >
                        $ {fmt(view.totalBudget - view.totalConsumed)}
                      </span>
                    </td>
                    <td className="py-2 px-2">
                      <div className="h-2 bg-[#f1ead7] relative overflow-hidden">
                        <div
                          className="h-2"
                          style={{
                            width: `${Math.min(view.totalPct, 100)}%`,
                            backgroundColor: LEVEL_BAR[view.totalLevel],
                          }}
                        />
                      </div>
                      <div
                        className="text-[10px] mt-0.5 tabular-nums"
                        style={{ color: LEVEL_BAR[view.totalLevel] }}
                      >
                        {view.totalPct.toFixed(1)}%
                      </div>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <span
                        className="text-[10px] uppercase tracking-widest px-2 py-1"
                        style={{
                          color: LEVEL_BAR[view.totalLevel],
                          borderBottom: `2px solid ${LEVEL_BAR[view.totalLevel]}`,
                        }}
                      >
                        {LEVEL_LABEL[view.totalLevel]}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="text-xs text-[#8b7e5e]">
              Define un monto en cada categoría. Las alertas se activan a {Number(thresholds.warning).toFixed(0)}% y al alcanzar {Number(thresholds.critical).toFixed(0)}%.
            </div>
            <button
              data-testid={BUDGETS.saveButton}
              onClick={onSave}
              disabled={saving}
              className="flex items-center gap-2 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] px-5 py-2.5 text-xs uppercase tracking-widest disabled:opacity-60"
            >
              <Save size={14} />
              {saving ? "Guardando..." : `Guardar presupuestos ${dirtyCount ? `(${dirtyCount})` : ""}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
